import {
  Box,
  IconButton,
  useTheme,
  Drawer,
  Typography,
  Button,
  Divider,
} from "@mui/material";
import { useState, useContext, useEffect, useCallback } from "react";
import { ColorModeContext } from "../../theme";
import { tokens } from "../../theme";
import LightModeOutlinedIcon from "@mui/icons-material/LightModeOutlined";
import DarkModeOutlinedIcon from "@mui/icons-material/DarkModeOutlined";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import PersonOutlinedIcon from "@mui/icons-material/PersonOutlined";
import { ExitToApp } from "@mui/icons-material";
import { ReactSession } from "react-client-session";
import { useNavigate } from "react-router-dom";

const Topbar = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const colorMode = useContext(ColorModeContext);
  const navigate = useNavigate();
  const [drawerOpen, setDrawerOpen] = useState(false);

  const INACTIVITY_TIMEOUT = 30 * 60 * 1000;

  const handleLogout = useCallback(() => {
    ReactSession.set("id_user", "");
    ReactSession.set("username", "");
    ReactSession.set("role", "");
    window.location.reload();
    navigate("/login", { replace: true });
  }, [navigate]);

  // useEffect(() => {
  //   const handleOnline = () => {
  //     console.log("Back online");
  //     // You can add additional actions here if needed
  //   };

  //   const handleOffline = () => {
  //     console.log("Gone offline");
  //     handleLogout();
  //   };

  //   window.addEventListener("online", handleOnline);
  //   window.addEventListener("offline", handleOffline);

  //   // Check initial state
  //   if (!navigator.onLine) {
  //     handleLogout();
  //   }

  //   // Cleanup
  //   return () => {
  //     window.removeEventListener("online", handleOnline);
  //     window.removeEventListener("offline", handleOffline);
  //   };
  // }, [handleLogout]);

  useEffect(() => {
    let inactivityTimer;

    const resetInactivityTimer = () => {
      clearTimeout(inactivityTimer);
      inactivityTimer = setTimeout(handleLogout, INACTIVITY_TIMEOUT);
    };

    // Set up event listeners for user activity
    const activityEvents = ["mousedown", "keydown", "touchstart", "scroll"];
    activityEvents.forEach((event) => {
      window.addEventListener(event, resetInactivityTimer);
    });

    // Initial setup of inactivity timer
    resetInactivityTimer();

    // Cleanup function
    return () => {
      activityEvents.forEach((event) => {
        window.removeEventListener(event, resetInactivityTimer);
      });
      clearTimeout(inactivityTimer);
    };
  }, [handleLogout]);

  const toggleDrawer = (open) => (event) => {
    if (
      event.type === "keydown" &&
      (event.key === "Tab" || event.key === "Shift")
    ) {
      return;
    }
    setDrawerOpen(open);
  };

  const drawerContent = (
    <Box
      role="presentation"
      p={2}
      onClick={toggleDrawer(false)}
      onKeyDown={toggleDrawer(false)}
      width={300}
      sx={{
        backgroundColor: colors.primary[400],
        color: colors.grey[100],
        height: "100%",
      }}
    >
      <Typography variant="h4" fontWeight="bold" mb={2}>
        Settings
      </Typography>
      <Divider sx={{ borderColor: colors.grey[300], my: 2 }} />
      <Typography variant="h5" fontWeight="bold" mb={1}>
        Mode
      </Typography>
      <Box display="flex" justifyContent="space-between" mb={2}>
        <Button
          variant={theme.palette.mode === "light" ? "contained" : "outlined"}
          startIcon={<LightModeOutlinedIcon />}
          onClick={() => {
            if (theme.palette.mode !== "light") {
              colorMode.toggleColorMode();
            }
          }}
          sx={{
            backgroundColor:
              theme.palette.mode === "light"
                ? colors.blueAccent[700]
                : "transparent",
            color: colors.grey[100],
            borderColor: colors.grey[100],
            "&:hover": {
              backgroundColor:
                theme.palette.mode === "light"
                  ? colors.blueAccent[800]
                  : colors.primary[300],
            },
          }}
        >
          Light
        </Button>
        <Button
          variant={theme.palette.mode === "dark" ? "contained" : "outlined"}
          startIcon={<DarkModeOutlinedIcon />}
          onClick={() => {
            if (theme.palette.mode !== "dark") {
              colorMode.toggleColorMode();
            }
          }}
          sx={{
            backgroundColor:
              theme.palette.mode === "dark"
                ? colors.blueAccent[700]
                : "transparent",
            color: colors.grey[100],
            borderColor: colors.grey[100],
            "&:hover": {
              backgroundColor:
                theme.palette.mode === "dark"
                  ? colors.blueAccent[800]
                  : colors.primary[300],
            },
          }}
        >
          Dark
        </Button>
      </Box>
    </Box>
  );

  return (
    <Box display="flex" justifyContent="flex-end" p={2}>
      <IconButton onClick={toggleDrawer(true)}>
        <SettingsOutlinedIcon />
      </IconButton>
      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={toggleDrawer(false)}
        PaperProps={{
          sx: {
            backgroundColor: colors.primary[400],
            color: colors.grey[100],
          },
        }}
      >
        {drawerContent}
      </Drawer>
      <IconButton>
        <PersonOutlinedIcon />
      </IconButton>
      <IconButton onClick={handleLogout}>
        <ExitToApp />
      </IconButton>
    </Box>
  );
};

export default Topbar;
